/**
 * ZaloPersonalApiService — tập hợp toàn bộ endpoint Zalo Personal.
 *
 * Port từ FE13:
 * db.zlpsHandleUser → /chat/zalopersonal/user
 * db.sendCard → /chat/zalopersonal/sendcard
 * db.updateZlpsGroup → /chat/zalopersonal/updategroup
 * db.listFriendEvent → /chat/zalopersonal/listfriendevent
 * db.changeFriendAlias → /chat/zalopersonal/changefriendalias
 * db.disperseGroup → /chat/zalopersonal/dispersegroup
 * db.deleteChat → /chat/zalopersonal/deletechat
 *
 * ⚠️ Tất cả endpoint này trả về dạng raw JSON (chưa v2 wrapper).
 * FE21 parse trực tiếp response — KHÔNG đi qua IResponseApi<T>.
 * Ngoại lệ (API v2, trả IResponseApi<T>): /chat/getrelatedfriendgroup.
 * Khi BE nâng v2, cần tạo action mới + cập nhật method tương ứng (§3.5).
 */
import {Injectable} from '@angular/core';
import {ApiService} from '@core/services/api.service';
import {Observable} from 'rxjs';
import {IResponseApi} from '@core/models';

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface IZlpsHandleUserParams {
  pageId: string;
  userId?: string | string[];
  act: string;
  /** Áp dụng cho act = 'send_friend_request' / 'leave_group' */
  text?: string;
  silent?: boolean;
  /** Áp dụng cho act = 'search_user_by_phone' */
  phone?: string;

  /** Áp dụng cho get_group_link / enable_group_link / disable_group_link */
  [key: string]: unknown;
}

export interface IZlpsUserResult {
  success?: boolean;
  data?: IZlpsUserData;
  items?: IZlpsGroupMember[];
}

export interface IZlpsUserData {
  userId?: string;
  displayName?: string;
  alias?: string;
  name?: string;
  avatar?: string;
  avt?: string;
  isFr?: boolean;
  isBlocked?: number;
  gender?: number;
  sdob?: string;
  phoneNumber?: string;
  /** Nhóm: mảng userId admin */
  adminIds?: string[];
  creatorId?: string;
  memVerList?: string[];
  pendingApprove?: { uids?: string[] };
  setting?: IZlpsGroupSetting;
  groupId?: string;
  link?: string;
  enabled?: 0 | 1;
  uid?: string;
  display_name?: string;

  [key: string]: unknown;
}

type ZlpsRecord = Record<string, unknown>;

/**
 * ApiService bọc response cũ thành `{code, data}`, trong khi provider Zalo lại
 * trả `{success, data}`. Toàn bộ UI Zalo Personal phải đọc qua helper này để
 * không vô tình render envelope thay vì profile thực tế.
 */
export function unwrapZlpsResponseData<T>(response: unknown): T | null {
  let value: unknown = response;
  for (let depth = 0; depth < 6; depth += 1) {
    if (!isZlpsRecord(value)) break;
    if (!('data' in value) || !('code' in value || 'success' in value)) break;
    value = value['data'];
  }
  return value === null || value === undefined ? null : value as T;
}

/** Chuẩn hoá field provider (uid/display_name/zalo_name) về model UI dùng chung. */
export function normalizeZlpsUserData(value: unknown): IZlpsUserData | null {
  if (!isZlpsRecord(value)) return null;
  const userId = firstZlpsString(value, 'userId', 'uid', 'fanpageUserId', 'groupId', 'id');
  // List endpoints do not all use the profile field name: recent conversations
  // return fanpageUserName, while group lists commonly return groupName.
  const displayName = firstZlpsString(
    value,
    'displayName', 'display_name', 'zaloName', 'zalo_name',
    'fanpageUserName', 'groupName', 'group_name', 'name', 'username',
  );
  const avatar = firstZlpsString(value, 'avatar', 'avt', 'customerAvatar');
  return {
    ...value,
    ...(userId ? {userId} : {}),
    ...(displayName ? {displayName, name: String(value['name'] || displayName)} : {}),
    ...(avatar ? {avatar} : {}),
  } as IZlpsUserData;
}

export function getZlpsResponseItems<T>(response: unknown): T[] {
  let value: unknown = unwrapZlpsResponseData<unknown>(response);
  // Accept both provider formats: data[] and data:{items:[]}, including
  // an additional nested data/result envelope from the API proxy.
  for (let depth = 0; depth < 6; depth += 1) {
    if (Array.isArray(value)) return value as T[];
    if (!isZlpsRecord(value)) return [];
    // get_group_members_info trả `profiles` / `changed_profiles` thay vì `items`.
    // FE1 đọc Object.values của hai field này; không xử lý chúng làm UI nhóm
    // rơi vào trạng thái spinner vô hạn dù request thành công.
    for (const key of ['items', 'profiles', 'changed_profiles', 'changedProfiles']) {
      const collection = value[key];
      if (Array.isArray(collection)) {
        if (collection.length) return collection as T[];
      }
      if (isZlpsRecord(collection)) {
        const entries = Object.values(collection);
        if (entries.length && entries.every(isZlpsRecord)) return entries as T[];
      }
    }
    const nested = value['data'] ?? value['result'] ?? value['items'];
    if (nested === undefined || nested === value) break;
    value = nested;
  }
  // Some Zalo Personal endpoints return a dictionary keyed by userId/groupId.
  // FE1 rendered Object.values(res.data) for this case.
  if (isZlpsRecord(value)) {
    const entries = Object.values(value);
    if (entries.length > 0 && entries.every(isZlpsRecord)) return entries as T[];
  }
  return [];
}

export function isZlpsResponseSuccessful(response: unknown): boolean {
  let value: unknown = response;
  let isSuccess = false;
  for (let depth = 0; depth < 3 && isZlpsRecord(value); depth += 1) {
    if (value['success'] === false || value['code'] === 0) return false;
    if (value['success'] === true || value['code'] === 1) isSuccess = true;
    if (!('data' in value) || !('code' in value || 'success' in value)) break;
    value = value['data'];
  }
  return isSuccess;
}

/**
 * Provider Zalo Personal thường trả HTTP 200 cho lỗi nghiệp vụ, ví dụ
 * `{error: 'User đã là bạn bè'}`. Lấy message từ các envelope phổ biến để UI
 * không biến lỗi cụ thể thành toast lỗi chung.
 */
export function getZlpsResponseError(response: unknown): string {
  const candidates: unknown[] = [response];

  for (let index = 0; index < candidates.length && index < 8; index += 1) {
    const value = candidates[index];
    if (!isZlpsRecord(value)) continue;

    for (const key of ['error', 'message', 'msg']) {
      const message = value[key];
      if (typeof message === 'string' && message.trim()) return message.trim();
      if (isZlpsRecord(message)) candidates.push(message);
    }

    for (const key of ['data', 'response']) {
      if (isZlpsRecord(value[key])) candidates.push(value[key]);
    }
  }

  return '';
}

function isZlpsRecord(value: unknown): value is ZlpsRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function firstZlpsString(value: ZlpsRecord, ...keys: string[]): string {
  for (const key of keys) {
    const candidate = value[key];
    if (candidate !== null && candidate !== undefined && String(candidate).trim()) return String(candidate);
  }
  return '';
}

export interface IZlpsGroupSetting {
  blockName?: number;
  setTopicOnly?: number;
  lockCreatePost?: number;
  lockCreatePoll?: number;
  lockSendMsg?: number;
  joinAppr?: number;
  signAdminMsg?: number;
  enableMsgHistory?: number;
}

export interface IZlpsGroupMember {
  userId?: string;
  displayName?: string;
  groupName?: string;
  group_name?: string;
  alias?: string;
  avatar?: string;
  avt?: string;
  phoneNumber?: string;
  fanpageUserId?: string;
  fanpageUserName?: string;
  groupId?: string;
  uid?: string;
  name?: string;
  type?: number;

  [key: string]: unknown;
}

export interface IZlpsGroupLinkResult {
  data?: { link?: string; enabled?: 0 | 1 };
}

export interface IZlpsFriendEvent {
  id: string;
  pageId: string;
  userId: string;
  name: string;
  avatar: string;
  message: string;
  createdAt: number;
}

export interface IZlpsListFriendEventResponse {
  items: IZlpsFriendEvent[];
}

export interface IZlpsUpdateGroupParams {
  pageId: string;
  conversationId?: string;
  groupName?: string;
  /** JSON.stringify(options) cho updateGroupSettings */
  options?: string;

  [key: string]: unknown;
}

export interface IChangeFriendAliasParams {
  pageId: string;
  friendId: string;
  name: string;
  alias: string;
}

export interface IChangeFriendAliasResponse {
  data?: { alias?: string; [key: string]: unknown };
}

export interface IZlpsDisperseGroupParams {
  pageId: string;
  groupId: string;
}

export interface IZlpsDeleteChatParams {
  pageId: string;
  lastMessage?: string;
  threadId?: string;
  conversationId?: string;
  /** 1 = group, 0 = inbox */
  type?: 0 | 1;
}

export interface IZlpsRelatedFriendGroupParams {
  pageId: string;
  /** UID Zalo của 1 bạn (BE chuyển sang server Zalo cá nhân dạng friendIds chuỗi) */
  friendId: string;
}

/** data của /chat/getrelatedfriendgroup: friendId → danh sách groupId chung. */
export interface IZlpsRelatedFriendGroupData {
  groupRelateds: Record<string, string[]>;
}

export interface IZlpsSendCardParams {
  pageId: string;
  conversationId?: string;
  targetUserId: string;
  /** JSON.stringify(cardsToSend) — FE13: db.sendCard({pageId, cardsInfo}) */
  cardsInfo?: string;

  [key: string]: unknown;
}

export type ZaloMobileSyncAction = 'start' | 'resume' | 'cancel' | 'status';
export type ZaloMobileSyncMode = 'auto' | 'full' | 'range';

export interface IZaloMobileSyncParams {
  pageId: string;
  act?: ZaloMobileSyncAction;
  mode?: ZaloMobileSyncMode;
  fromTs?: number | string;
  toTs?: number | string;
}

export interface IZaloMobileSyncState {
  syncId?: string;
  stage?: string;
  progress?: number;
  message?: string;
  error?: string;
  resumable?: boolean;
  pendingExpiresAt?: number;
  syncedTs?: number;
  range?: {fromTs?: number | string; toTs?: number | string};
  stats?: Record<string, number>;
  startedAt?: number;
  finishedAt?: number;
  updatedAt?: number;
}

export interface IZaloMobileSyncStatus extends IZaloMobileSyncState {
  server?: IZaloMobileSyncState | null;
  pending?: IZaloMobileSyncState | null;
  local?: IZaloMobileSyncState | null;
}

export interface IZaloMobileSyncResponse {
  code?: number;
  success?: boolean;
  error?: string;
  messages?: string[];
  data?: IZaloMobileSyncStatus;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({providedIn: 'root'})
export class ZaloPersonalApiService extends ApiService {

  connectZalo(zaloId: string): Observable<unknown> {
    return this.testZlps('/api/login/connectzalo', {zaloId});
  }

  checkListener(zaloId: string): Observable<unknown> {
    return this.testZlps('/api/login/checklistener', {zaloId});
  }

  mobileSync(params: IZaloMobileSyncParams): Observable<IZaloMobileSyncResponse> {
    // Local Zalo bridge is available only while developing; production always uses the main BE.
    // if (!environment.production) {
    //   const {pageId, ...syncParams} = params;
    //   return this.postRequestLocal<IZaloMobileSyncResponse>(
    //     'http://localhost:3000/api/mobilesync/start',
    //     {
    //       zaloId: pageId.replace('zlp_', ''),
    //       ...syncParams,
    //     },
    //   );
    // }

    return this.postRequest<IZaloMobileSyncResponse>('/chat/zalopersonal/mobilesync', params);
  }

  /** Cập nhật nhân viên gửi cho các tin nhiều ảnh đã được webhook lưu, giống FE13. */
  updateMessages(params: {pageId: string; messages: string}): Observable<unknown> {
    return this.postRequest<unknown>('/chat/zalopersonal/updatemessages', params);
  }

  /**
   * Đa năng: tìm user, lấy danh sách gần đây/nhóm/bạn bè, gửi kết bạn,
   * chấp nhận kết bạn, rời nhóm, lấy/bật/tắt group link, xóa thành viên…
   * Truyền `act` để chọn action tương ứng ở BE.
   */
  handleUser(params: IZlpsHandleUserParams): Observable<IZlpsUserResult> {
    return this.postRequest<IZlpsUserResult>('/chat/zalopersonal/user', params);
  }

  /**
   * Chia sẻ danh thiếp (Zalo Personal send card).
   */
  sendCard(params: IZlpsSendCardParams): Observable<unknown> {
    return this.postRequest<unknown>('/chat/zalopersonal/sendcard', params);
  }

  /**
   * Đổi tên nhóm hoặc cập nhật cài đặt nhóm.
   */
  updateGroup(params: IZlpsUpdateGroupParams): Observable<unknown> {
    return this.postRequest<unknown>('/chat/zalopersonal/updategroup', params);
  }

  /**
   * Danh sách lời mời kết bạn theo pageId.
   */
  listFriendEvent(params: { pageIds: string[] }): Observable<IZlpsListFriendEventResponse> {
    return this.postRequest<IZlpsListFriendEventResponse>('/chat/zalopersonal/listfriendevent', params);
  }

  /**
   * Đặt/sửa tên gợi nhớ cho một liên hệ Zalo Personal.
   */
  changeFriendAlias(params: IChangeFriendAliasParams): Observable<IChangeFriendAliasResponse> {
    return this.postRequest<IChangeFriendAliasResponse>('/chat/zalopersonal/changefriendalias', params);
  }

  /**
   * Giải tán nhóm (chỉ trưởng nhóm mới thực hiện được).
   */
  disperseGroup(params: IZlpsDisperseGroupParams): Observable<unknown> {
    return this.postRequest<unknown>('/chat/zalopersonal/dispersegroup', params);
  }

  /**
   * Xóa hội thoại (inbox hoặc nhóm).
   */
  deleteChat(params: IZlpsDeleteChatParams): Observable<unknown> {
    return this.postRequest<unknown>('/chat/zalopersonal/deletechat', params);
  }

  /**
   * Lấy id các nhóm chung với 1 hoặc nhiều bạn Zalo (API v2, trả IResponseApi).
   */
  getRelatedFriendGroup(params: IZlpsRelatedFriendGroupParams): Observable<IResponseApi<IZlpsRelatedFriendGroupData>> {
    return this.postRequest<IResponseApi<IZlpsRelatedFriendGroupData>>('/chat/getrelatedfriendgroup', params);
  }

  // test Calo cá nhân ở local
  testZlps(endpoint: string, params: any): Observable<unknown> {
    const AUTH_CALL = "{\"imei\":\"3e326297-2de7-4432-bde2-4d3bbc2b259e-89db729cfcdc129111f017b0e7ac324a\",\"cookies\":\"{\\\"version\\\":\\\"tough-cookie@5.1.2\\\",\\\"storeType\\\":\\\"MemoryCookieStore\\\",\\\"rejectPublicSuffixes\\\":true,\\\"enableLooseMode\\\":false,\\\"allowSpecialUseDomain\\\":true,\\\"prefixSecurity\\\":\\\"silent\\\",\\\"cookies\\\":[{\\\"key\\\":\\\"nl_b04af40bb0e193acf8a9877592394ada\\\",\\\"value\\\":\\\"tzaoLC8i6lt5rJjMmYyH-yhMEqdNTtI-cz4P0ZenVG\\\",\\\"maxAge\\\":5,\\\"domain\\\":\\\"id.zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"sameSite\\\":\\\"lax\\\"},{\\\"key\\\":\\\"zpdid\\\",\\\"value\\\":\\\"413-aLpng3qT592IN_d1D1yKavbPzi0-\\\",\\\"maxAge\\\":157680000,\\\"domain\\\":\\\"id.zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"sameSite\\\":\\\"lax\\\"},{\\\"key\\\":\\\"zlogin_session\\\",\\\"value\\\":\\\"kW4JGLyjCnIxFnDDLXTbH-Tj2K5V5ML4x6iTLWLMQrodAWj25LLgNA0e45uGKcOJ\\\",\\\"maxAge\\\":3600,\\\"domain\\\":\\\"id.zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"sameSite\\\":\\\"lax\\\"},{\\\"key\\\":\\\"_zlang\\\",\\\"value\\\":\\\"vn\\\",\\\"expires\\\":\\\"2026-08-17T05:35:47.000Z\\\",\\\"maxAge\\\":86400,\\\"domain\\\":\\\"zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:51.830Z\\\"},{\\\"key\\\":\\\"zpsid\\\",\\\"value\\\":\\\"S_x4.429833083.299.URf-fyW5bK-plWjMn0MLJxrsvtlwFAry-pMjVCRafu3ts2N9oIdPpg85bKy\\\",\\\"maxAge\\\":31536000,\\\"domain\\\":\\\"zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:51.830Z\\\",\\\"sameSite\\\":\\\"none\\\"},{\\\"key\\\":\\\"__zi\\\",\\\"value\\\":\\\"3000.QOBlzDCV2uGerkFzm0LKs6pHulJ01HlPOzge_S856DLdqQhm.1\\\",\\\"maxAge\\\":63072000,\\\"domain\\\":\\\"zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:51.830Z\\\",\\\"sameSite\\\":\\\"none\\\"},{\\\"key\\\":\\\"zlogtype\\\",\\\"value\\\":\\\"EXPIRED\\\",\\\"expires\\\":\\\"1970-01-01T00:00:00.000Z\\\",\\\"maxAge\\\":0,\\\"domain\\\":\\\"id.zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:48.662Z\\\"},{\\\"key\\\":\\\"zpsid\\\",\\\"value\\\":\\\"eMKnVcAlVqAZUYmFKfzgMDuHPW94c6jHtX5x04MIQpQrJdWpRgDpCeSqFJrov4umu1Cy6JhRSGpi3JWf3yL_PzqoAr8ysdHNwrC-HtkdEKkGULTOLVjSMKjv\\\",\\\"maxAge\\\":31536000,\\\"domain\\\":\\\"zaloapp.com\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"sameSite\\\":\\\"none\\\"},{\\\"key\\\":\\\"__zi\\\",\\\"value\\\":\\\"3000.QOBlzDCV2uGerkFzm0LKs6pHulJ01HlPOzge_S856DLargpw.1\\\",\\\"maxAge\\\":63072000,\\\"domain\\\":\\\"zaloapp.com\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"sameSite\\\":\\\"none\\\"},{\\\"key\\\":\\\"zpw_sek\\\",\\\"value\\\":\\\"Lcku.429833083.a0.wdN4ZjYSPd1RwUCT0L9z15jm66y2Q5z3OnzCTnWLDsDAVJzB47qeI4PdE7H7Rq0cNLoSDkzjeBIcKqr8eJXz10\\\",\\\"maxAge\\\":7776000,\\\"domain\\\":\\\"chat.zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"secure\\\":true,\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:48.662Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:51.830Z\\\",\\\"sameSite\\\":\\\"lax\\\"},{\\\"key\\\":\\\"app.event.zalo.me\\\",\\\"value\\\":\\\"5156303321863021575\\\",\\\"expires\\\":\\\"2026-08-18T05:35:49.000Z\\\",\\\"domain\\\":\\\"zalo.me\\\",\\\"path\\\":\\\"\\/\\\",\\\"httpOnly\\\":true,\\\"hostOnly\\\":false,\\\"creation\\\":\\\"2026-08-16T05:35:50.264Z\\\",\\\"lastAccessed\\\":\\\"2026-08-16T05:35:51.830Z\\\"}]}\",\"userAgent\":\"Mozilla\\/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit\\/537.36 (KHTML, like Gecko) Chrome\\/143.0.0.0 Safari\\/537.36\"}"
    params.auth = AUTH_CALL;

    // delete "/" in first character in endpoint
    if (endpoint.startsWith('/')) {
      endpoint = endpoint.substring(1);  // bỏ ký tự "/" đầu tiên
    }

    return this.postRequestLocal<unknown>('http://localhost:3000/' + endpoint, params);
  }
}
